import { DefaultTheme } from 'styled-components/macro';

const boxShadows = (color: [number, number, number]) => {
  const rgb = color.join(',');
  return {
    xs: `0 1px 3px rgba(${rgb},0.12), 0 1px 2px rgba(${rgb},0.24)`,
    sm: `0px 1px 9px 1.5px rgba(${rgb} 0.14)`,
    base: `0px 0px 6px rgba(${rgb}, 0.15), 0px 0px 6px rgba(${rgb}, 0.15)`,
    lg: `0 14px 28px rgba(${rgb},0.25), 0 10px 10px rgba(${rgb},0.22)`,
    xl: `0 19px 38px rgba(${rgb},0.30), 0 15px 12px rgba(${rgb},0.22)`,
  };
};

const theme: DefaultTheme = {
  deviceType: undefined,
  mediaQueries: {
    // orientation
    isLandscape: '@media (orientation: landscape)',
    isPortrait: '@media (orientation: portrait)',
    // device based
    isMobile: '@media (max-width: 1023px)',
    isMobileLandscape:
      '@media  (orientation: landscape) and (max-height: 639px)',
    isTablet: '@media (min-width: 640px) and (max-width: 1023px)',
    isDesktop: '@media (min-width: 1024px)',
    // dimension based
    sm: '@media (min-width: 640px)',
    base: '@media (min-width: 768px)',
    lg: '@media (min-width: 1024px)',
    xl: '@media (min-width: 1280px)',
    xxl: '@media (min-width: 1536px)',
  },
  fonts: {
    gtEesti: '"GT Eesti Display", sans-serif',
    literata: '"Literata", sans-serif',
    mono:
      'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  zIndexes: {
    absoluteUp: 999999,
    overlay: 200,
    modal: 100,
    popup: 50,
    menu: 40,
    sticky: 20,
    default: 0,
    background: -10,
    absoluteDown: -999999,
  },
  boxShadows: {
    ...boxShadows([0, 0, 0]),
    get: (
      size: 'xs' | 'sm' | 'base' | 'lg' | 'xl',
      color?: [number, number, number]
    ) => {
      return boxShadows(color ?? [0, 0, 0])[size];
    },
  },
  dimensions: {
    borderRadius: '10px',
    inputWidth: '300px',
    maxCardWidth: '800px',
    minCardHeight: '600px',
  },
  colors: {
    transparent: 'transparent',
    white: '#ffffff',
    gold: '#CBBF98',
    backgroundMain: '#FDFDFD',
    ice: '#F7F8FA',
    powder: '#FBFAF8',
    label: '#6A6A84',
    minimum: '#00CFDA',
    maximum: '#00DA79',
    olympia: '#6E7BA0',
    sky: '#ACEBFF',

    // objective types colors
    Expense: '#080833',
    Retirement: '#080833',
    SafetyNet: '#080833',
    GenericInvestment: '#080833',

    // service types colors
    Gestione: process.env.REACT_APP_COLORS_PRIMARY ?? '#5A3AEB',
    Collocamento: process.env.REACT_APP_COLORS_PRIMARY_90W ?? '#BFB0FF',
    Consulenza: process.env.REACT_APP_COLORS_TERTIARY_20B ?? '#F0A729',

    // instrument types colors
    mandate: '#F6C0CE',
    etf: '#F7ECB7',
    fund: '#C7C0E8',
    bond: '#B5D1FC',
    equity: '#D4EDC1',

    primary: process.env.REACT_APP_COLORS_PRIMARY ?? '#5A3AEB',
    primary20b: process.env.REACT_APP_COLORS_PRIMARY_20B ?? '#4A2CD6',
    primary40b: process.env.REACT_APP_COLORS_PRIMARY_40B ?? '#381EB1',
    primary60b: process.env.REACT_APP_COLORS_PRIMARY_60B ?? '#291589',
    primary20w: process.env.REACT_APP_COLORS_PRIMARY_20W ?? '#6C4EF1',
    primary40w: process.env.REACT_APP_COLORS_PRIMARY_40W ?? '#7B60F8',
    primary60w: process.env.REACT_APP_COLORS_PRIMARY_60W ?? '#8C74FB',
    primary80w: process.env.REACT_APP_COLORS_PRIMARY_80W ?? '#9E88FF',
    primary85w: process.env.REACT_APP_COLORS_PRIMARY_85W ?? '#B4A3FD',
    primary90w: process.env.REACT_APP_COLORS_PRIMARY_90W ?? '#BFB0FF',
    primary92w: process.env.REACT_APP_COLORS_PRIMARY_92W ?? '#D3CAFF',
    primary95w: process.env.REACT_APP_COLORS_PRIMARY_95W ?? '#E8E3FF',
    primary98w: process.env.REACT_APP_COLORS_PRIMARY_98W ?? '#F3F0FF',

    secondary: process.env.REACT_APP_COLORS_SECONDARY ?? '#080833',
    secondary20w: process.env.REACT_APP_COLORS_SECONDARY_20W ?? '#39395B',
    secondary40w: process.env.REACT_APP_COLORS_SECONDARY_40W ?? '#6A6A84',
    secondary60w: process.env.REACT_APP_COLORS_SECONDARY_60W ?? '#9C9CAD',
    secondary80w: process.env.REACT_APP_COLORS_SECONDARY_80W ?? '#CDCDD6',
    secondary90w: process.env.REACT_APP_COLORS_SECONDARY_90W ?? '#E6E6EB',
    secondary95w: process.env.REACT_APP_COLORS_SECONDARY_95W ?? '#F2F2F4',
    secondary98w: process.env.REACT_APP_COLORS_SECONDARY_98W ?? '#F7F7F8',

    tertiary: process.env.REACT_APP_COLORS_TERTIARY ?? '#F2B347',
    tertiary20b: process.env.REACT_APP_COLORS_TERTIARY_20B ?? '#F0A729',
    tertiary40b: process.env.REACT_APP_COLORS_TERTIARY_40B ?? '#DA8E0B',
    tertiary60b: process.env.REACT_APP_COLORS_TERTIARY_60B ?? '#C17A00',

    black: '#111111',
    black20w: '#333333',
    black40w: '#666666',
    black60w: '#999999',
    black80w: '#CCCCCC',
    black90w: '#E6E6E6',
    black95w: '#F2F2F2',
    black98w: '#f7f7f7',

    success: '#2FD543',
    success20b: '#00AF13',
    success40b: '#008212',
    success60b: '#00570C',
    success20w: '#00DA16',
    success40w: '#00ED62',
    success60w: '#79F499',
    success80w: '#C1F9CD',
    success90w: '#E1FDE7',
    success95w: '#F4FCF5',
    success98w: '#F9FEFA',

    danger: '#FF2739',
    danger20b: '#DE0322',
    danger40b: '#A7021A',
    danger60b: '#6F0112',
    danger20w: '#FF3C5A',
    danger40w: '#FF7385',
    danger60w: '#FFA2AD',
    danger80w: '#FFD1D7',
    danger90w: '#FFE8EB',
    danger95w: '#FFF3F4',
    danger98w: '#FFFAFB',

    warning: '#F3C259',
    warning20b: '#CF9900',
    warning40b: '#9B7202',
    warning60b: '#684C05',
    warning20w: '#FFCC4C',
    warning40w: '#FFD97E',
    warning60w: '#FFE6AA',
    warning80w: '#FFF2D5',
    warning90w: '#FFF8EB',
    warning95w: '#FEFBF6',
    warning98w: '#FEFDFB',

    info: '#ACEBFF',

    chart1: process.env.REACT_APP_COLORS_PRIMARY ?? '#6F51D2',
    chart2: '#EF5080',
    chart3: '#E97F74',
    chart4: '#F2B347',
    chart5: '#F2DB2B',
    chart6: '#9FDB5E',
    chart7: '#13BC78',
    chart8: '#3871F5',
    chart9: '#AF51A9',
    chart10: '#EC687A',
    chart11: '#EE995E',
    chart12: '#F2C739',
    chart13: '#C9DB45',
    chart14: '#59CC6B',
    chart15: '#2697B7',
    chart16: '#5461E3',
    chart17: '#963680',
    chart18: '#D4485F',
    chart19: '#D47B3D',
    chart20: '#D9AD1E',
    chart21: '#B0C22B',
    chart22: '#3DB34F',
    chart23: '#117F9E',
    chart24: '#3644C9',
    chart25: '#7D2058',
    chart26: '#BA2D44',
  },
  lists: {
    colors: [
      process.env.REACT_APP_COLORS_PRIMARY ?? '#6F51D2',
      '#EF5080',
      '#E97F74',
      '#F2B347',
      '#F2DB2B',
      '#9FDB5E',
      '#13BC78',
      '#3871F5',
      '#AF51A9',
      '#EC687A',
      '#EE995E',
      '#F2C739',
      '#C9DB45',
      '#59CC6B',
      '#2697B7',
      '#5461E3',
      '#963680',
      '#D4485F',
      '#D47B3D',
      '#D9AD1E',
      '#B0C22B',
      '#3DB34F',
      '#117F9E',
      '#3644C9',
      '#7D2058',
      '#BA2D44',
    ],
  },
};

export default theme;
